import React, { useEffect, useMemo, useState } from "react";
import api from "../api";
import { getStoreProductTitle } from "../utils/productDisplay";

export const DEFAULT_SEARCH_TERMS = [
  "토너",
  "복합기 렌탈",
  "A3 컬러",
  "삼성 CLT",
  "HP 222A",
  "Canon CRG",
];

export function useSearchSuggestions(query, open, options = {}) {
  const { includeRental = true, limit = 8, debounceMs = 160 } = options;
  const [state, setState] = useState({
    products: [],
    categories: [],
    keywords: DEFAULT_SEARCH_TERMS,
    total: 0,
    loading: false,
  });

  useEffect(() => {
    const q = (query || "").trim();
    if (!open) return undefined;
    if (!q) {
      setState({
        products: [],
        categories: [],
        keywords: DEFAULT_SEARCH_TERMS,
        total: 0,
        loading: false,
      });
      return undefined;
    }

    const controller = new AbortController();
    const timer = setTimeout(async () => {
      setState((prev) => ({ ...prev, loading: true }));
      try {
        const res = await api.get("/api/store/search-suggestions", {
          params: { q, limit, include_rental: includeRental },
          signal: controller.signal,
        });
        setState({
          products: res.data.products || [],
          categories: res.data.categories || [],
          keywords: res.data.keywords?.length ? res.data.keywords : DEFAULT_SEARCH_TERMS,
          total: res.data.total || 0,
          loading: false,
        });
      } catch (err) {
        if (err.name === "CanceledError" || err.code === "ERR_CANCELED") return;
        setState({
          products: [],
          categories: [],
          keywords: DEFAULT_SEARCH_TERMS,
          total: 0,
          loading: false,
        });
      }
    }, debounceMs);

    return () => {
      clearTimeout(timer);
      controller.abort();
    };
  }, [query, open, includeRental, limit, debounceMs]);

  return state;
}

function formatPrice(product) {
  const saleType = product.sale_type || "sale";
  if (saleType === "rental" && product.rental_price > 0) return `월 ${Number(product.rental_price).toLocaleString()}원`;
  if (product.price > 0) return `${Number(product.price).toLocaleString()}원`;
  if (product.rental_price > 0) return `월 ${Number(product.rental_price).toLocaleString()}원`;
  return "가격문의";
}

function saleLabel(product) {
  if (product.stock_status === "soldout") return "품절";
  const saleType = product.sale_type || "sale";
  if (saleType === "rental") return "렌탈";
  if (saleType === "both") return "판매+렌탈";
  return "판매";
}

function metaLine(product) {
  return [product.brand, product.store_category, product.sub_category].filter(Boolean).join(" · ");
}

function detailLine(product) {
  if (product.compatible_models) return `호환: ${String(product.compatible_models).slice(0, 64)}`;
  if (product.subtitle) return String(product.subtitle).slice(0, 72);
  if (product.product_code) return product.product_code;
  return "";
}

export function SearchSuggestionsPanel({
  query,
  suggestions,
  activeIndex = -1,
  onPickProduct,
  onPickKeyword,
  onSubmit,
  style,
  compact = false,
}) {
  const q = (query || "").trim();
  const products = suggestions.products || [];
  const categories = suggestions.categories || [];
  const keywords = suggestions.keywords?.length ? suggestions.keywords : DEFAULT_SEARCH_TERMS;
  const showProducts = q && products.length > 0;

  const panelStyle = useMemo(() => ({
    position: "absolute",
    top: "calc(100% + 10px)",
    left: 0,
    width: compact ? "min(420px, calc(100vw - 24px))" : "min(560px, calc(100vw - 24px))",
    zIndex: 2000,
    borderRadius: 18,
    background: "rgba(13,17,29,0.96)",
    border: "1px solid rgba(255,255,255,0.12)",
    boxShadow: "0 24px 80px rgba(0,0,0,0.42)",
    backdropFilter: "blur(24px) saturate(160%)",
    WebkitBackdropFilter: "blur(24px) saturate(160%)",
    overflow: "hidden",
    color: "white",
    ...style,
  }), [compact, style]);

  return (
    <div style={panelStyle} role="listbox" aria-label="검색 제안">
      <div style={{ padding: compact ? "10px" : "12px" }}>
        {suggestions.loading && (
          <div style={{ padding: 16, fontSize: 12, color: "rgba(255,255,255,0.58)" }}>
            검색 중...
          </div>
        )}

        {!suggestions.loading && !q && (
          <div>
            <div style={{ padding: "4px 4px 10px", fontSize: 11, color: "rgba(255,255,255,0.48)", fontWeight: 700 }}>
              빠른 검색
            </div>
            <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
              {keywords.map((keyword) => (
                <button
                  key={keyword}
                  type="button"
                  onMouseDown={(e) => e.preventDefault()}
                  onClick={() => onPickKeyword?.(keyword)}
                  style={{
                    padding: "7px 10px",
                    borderRadius: 999,
                    background: "rgba(255,255,255,0.08)",
                    color: "rgba(255,255,255,0.82)",
                    fontSize: 12,
                    fontWeight: 650,
                  }}
                >
                  {keyword}
                </button>
              ))}
            </div>
          </div>
        )}

        {!suggestions.loading && q && (
          <>
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", gap: 12, padding: "2px 4px 10px" }}>
              <div style={{ fontSize: 12, color: "rgba(255,255,255,0.58)" }}>
                <strong style={{ color: "white" }}>{q}</strong>
                {suggestions.total > 0 ? ` 결과 ${suggestions.total}개` : " 결과 없음"}
              </div>
              <button
                type="button"
                onMouseDown={(e) => e.preventDefault()}
                onClick={onSubmit}
                style={{ fontSize: 12, color: "#70b8ff", fontWeight: 700, whiteSpace: "nowrap" }}
              >
                전체 보기
              </button>
            </div>

            {showProducts ? (
              <div
                style={{
                  display: "grid",
                  gap: 6,
                  maxHeight: compact ? 318 : "min(420px, calc(100vh - 260px))",
                  overflowY: "auto",
                  overscrollBehavior: "contain",
                  paddingRight: 2,
                }}
              >
                {products.map((product, index) => (
                  <button
                    key={product.id || product.slug}
                    type="button"
                    role="option"
                    aria-selected={activeIndex === index}
                    onMouseDown={(e) => e.preventDefault()}
                    onClick={() => onPickProduct?.(product)}
                    style={{
                      display: "grid",
                      gridTemplateColumns: compact ? "44px 1fr" : "52px 1fr auto",
                      gap: 10,
                      alignItems: "center",
                      width: "100%",
                      textAlign: "left",
                      padding: compact ? "8px" : "10px",
                      borderRadius: 14,
                      background: activeIndex === index ? "rgba(0,113,227,0.22)" : "rgba(255,255,255,0.055)",
                      border: activeIndex === index ? "1px solid rgba(112,184,255,0.42)" : "1px solid transparent",
                    }}
                  >
                    <div style={{
                      width: compact ? 44 : 52,
                      height: compact ? 44 : 52,
                      borderRadius: 12,
                      background: "rgba(255,255,255,0.9)",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      overflow: "hidden",
                    }}>
                      {product.image_url ? (
                        <img src={product.image_url} alt="" style={{ width: "86%", height: "86%", objectFit: "contain" }} loading="lazy" />
                      ) : (
                        <svg width="22" height="22" fill="none" stroke="#8e8e93" strokeWidth="1.4" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" d="M6 9V4.5A1.5 1.5 0 017.5 3h9A1.5 1.5 0 0118 4.5V9m-12 9h12m-12 0l-.75 3h13.5L18 18m-12 0H4.5A1.5 1.5 0 013 16.5v-6A1.5 1.5 0 014.5 9h15a1.5 1.5 0 011.5 1.5v6a1.5 1.5 0 01-1.5 1.5H18" />
                        </svg>
                      )}
                    </div>
                    <div style={{ minWidth: 0 }}>
                      <div style={{ display: "flex", alignItems: "center", gap: 6, minWidth: 0 }}>
                        <span style={{
                          flex: "0 0 auto",
                          padding: "2px 6px",
                          borderRadius: 999,
                          background: "rgba(112,184,255,0.14)",
                          color: "#9fceff",
                          fontSize: 10,
                          fontWeight: 800,
                        }}>
                          {product.stock_status === "soldout" ? "품절" : (product.match_reason || saleLabel(product))}
                        </span>
                        <span style={{
                          flex: "1 1 auto",
                          minWidth: 0,
                          color: "white",
                          fontSize: compact ? 12 : 13,
                          fontWeight: 750,
                          lineHeight: 1.25,
                          whiteSpace: "normal",
                        }}>
                          {getStoreProductTitle(product)}
                        </span>
                      </div>
                      <div style={{
                        marginTop: 4,
                        color: "rgba(255,255,255,0.56)",
                        fontSize: 11,
                        overflow: "hidden",
                        textOverflow: "ellipsis",
                        whiteSpace: "nowrap",
                      }}>
                        {metaLine(product)}
                      </div>
                      {!compact && detailLine(product) && (
                        <div style={{
                          marginTop: 3,
                          color: "rgba(255,255,255,0.42)",
                          fontSize: 11,
                          overflow: "hidden",
                          textOverflow: "ellipsis",
                          whiteSpace: "nowrap",
                        }}>
                          {detailLine(product)}
                        </div>
                      )}
                    </div>
                    {!compact && (
                      <div style={{ color: "#fff", fontSize: 12, fontWeight: 800, whiteSpace: "nowrap" }}>
                        {formatPrice(product)}
                      </div>
                    )}
                  </button>
                ))}
              </div>
            ) : (
              <div style={{ padding: "18px 4px 14px", color: "rgba(255,255,255,0.56)", fontSize: 13 }}>
                일치하는 상품이 없습니다. 모델명 일부나 브랜드명으로 다시 검색해 보세요.
              </div>
            )}

            {(categories.length > 0 || keywords.length > 0) && (
              <div style={{ borderTop: "1px solid rgba(255,255,255,0.08)", marginTop: 10, paddingTop: 10 }}>
                <div style={{ display: "flex", gap: 7, flexWrap: "wrap" }}>
                  {categories.map((category) => (
                    <button
                      key={category.name}
                      type="button"
                      onMouseDown={(e) => e.preventDefault()}
                      onClick={() => onPickKeyword?.(category.name)}
                      style={{
                        padding: "6px 9px",
                        borderRadius: 999,
                        background: "rgba(255,255,255,0.07)",
                        color: "rgba(255,255,255,0.72)",
                        fontSize: 11,
                        fontWeight: 700,
                      }}
                    >
                      {category.name} {category.count}
                    </button>
                  ))}
                  {keywords.slice(0, compact ? 3 : 5).map((keyword) => (
                    <button
                      key={keyword}
                      type="button"
                      onMouseDown={(e) => e.preventDefault()}
                      onClick={() => onPickKeyword?.(keyword)}
                      style={{
                        padding: "6px 9px",
                        borderRadius: 999,
                        background: "rgba(255,255,255,0.05)",
                        color: "rgba(255,255,255,0.62)",
                        fontSize: 11,
                        fontWeight: 650,
                      }}
                    >
                      {keyword}
                    </button>
                  ))}
                </div>
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
}
